import {
  Fragment,
  useEffect,
  useMemo,
  useState,
  type ComponentProps,
} from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Flex,
  Popover,
  Text,
  ToggleButton,
  ToggleButtonGroup,
} from '@backstage/ui';
import {
  InsightEntity,
  scanLabel,
} from '@internal/backstage-plugin-catalog-devops-common';
import {
  PageSkeleton,
  useSelectPopover,
} from '@internal/backstage-plugin-devops-react';
import { useInsightsContext } from '../Providers';
import { RegionChip } from '../RegionChip';
import { ModernCardField, ModernRightDrawer } from '../Drawers';
import styles from '../insights-page.module.css';

const STATUS_OPTIONS = ['Active', 'Resolved', 'Archived'] as const;
type StatusFilter = '' | (typeof STATUS_OPTIONS)[number];

const TABLE_STEP = 14;

/** Resource-type bar colors from the mockup; unknown types fall back to gray. */
const TYPE_COLORS: Record<string, string> = {
  'S3 Bucket': '#0d74ce',
  'IAM Role': '#7c5cbf',
  'KMS Key': '#0d8a4f',
  'SQS Queue': '#b45309',
  'ECR Repository': '#8a3a8a',
  'SNS Topic': '#7a6a00',
};

const statusPillClass = (status: string): string => {
  if (status === 'Active') {
    return styles.pillActive;
  }
  if (status === 'Resolved') {
    return styles.pillResolved;
  }
  return styles.pillArchived;
};

const statusPill = (status: string) => (
  <span className={`${styles.pill} ${statusPillClass(status)}`}>{status}</span>
);

/** Clickable horizontal bar (region / resource-type facets). */
function FacetBar(props: {
  label: string;
  count: number;
  max: number;
  selected: boolean;
  onToggle: () => void;
  color?: string;
}) {
  const { label, count, max, selected, onToggle, color } = props;
  return (
    <div className={styles.barRow}>
      <button
        type="button"
        className={`${styles.barLabel} ${selected ? styles.barLabelOn : ''}`}
        onClick={onToggle}
        title={label}
      >
        {label}
      </button>
      <button
        type="button"
        className={styles.barTrack}
        onClick={onToggle}
        aria-label={`${selected ? 'Remove filter' : 'Filter by'} ${label}`}
      >
        <i
          className={styles.barFill}
          style={{
            width: `${Math.round((count / Math.max(1, max)) * 100)}%`,
            ...(color ? { background: color } : {}),
          }}
        />
      </button>
      <span className={styles.barCount}>{count}</span>
    </div>
  );
}

function FindingPickerPopover(props: {
  options: string[];
  selected: string[];
  counts: Record<string, number>;
  onToggle: (value: string) => void;
  popoverProps: Omit<ComponentProps<typeof Popover>, 'children'>;
}) {
  const { options, selected, counts, onToggle, popoverProps } = props;
  return (
    <Popover {...popoverProps} placement="bottom start" hideArrow>
      <div className={styles.popover}>
        {options.map(option => {
          const zero = counts[option] === 0 && !selected.includes(option);
          return (
            <label
              key={option}
              className={zero ? styles.popoverZero : undefined}
            >
              <input
                type="checkbox"
                checked={selected.includes(option)}
                onChange={() => onToggle(option)}
              />
              {option}
              <span className={styles.popoverCount}>{counts[option]}</span>
            </label>
          );
        })}
      </div>
    </Popover>
  );
}

/**
 * Insights tab, 1:1 with design/insights-page-mockup.html: stat strip,
 * status/search/type/region toolbar with faceted pickers, global and
 * regional tables, clickable region/type facet bars and a read-only finding
 * drawer (Archive/Resolve modeled disabled — needs new cloud-tools actions).
 * Deep links: ?st=Active&region=us-west-2.
 */
export function InsightsTab() {
  const location = useLocation();
  const navigate = useNavigate();
  const typeSelect = useSelectPopover();
  const regionSelect = useSelectPopover();
  const { globalInsights, regionalInsights, allRegions, allInsights, isLoading } =
    useInsightsContext();

  const [initialStatus, initialRegions] = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const st = params.get('st');
    const region = params.get('region');
    return [st ?? '', region ? [region] : []];
    // Deep links are read once on mount, like the other tabs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [status, setStatus] = useState<StatusFilter>(
    initialStatus as StatusFilter,
  );
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedRegions, setSelectedRegions] =
    useState<string[]>(initialRegions);
  const [query, setQuery] = useState('');
  const [selectedFinding, setSelectedFinding] = useState<InsightEntity>();
  const [visibleGlobal, setVisibleGlobal] = useState(TABLE_STEP);
  const [visibleRegional, setVisibleRegional] = useState(TABLE_STEP);

  // Facet counts recount against the OTHER active filters (mockup pattern).
  const facet = (field: 'type' | 'region', value: string) =>
    allInsights.filter(insight => {
      if (query && !insight.spec.resource.toLowerCase().includes(query.toLowerCase())) {
        return false;
      }
      if (status && insight.spec.status !== status) {
        return false;
      }
      if (
        selectedTypes.length > 0 &&
        field !== 'type' &&
        !selectedTypes.includes(insight.spec.resourceType)
      ) {
        return false;
      }
      if (
        selectedRegions.length > 0 &&
        field !== 'region' &&
        !selectedRegions.includes(insight.spec.region)
      ) {
        return false;
      }
      if (field === 'type') {
        return value === insight.spec.resourceType;
      }
      return value === insight.spec.region;
    }).length;

  const matches = (insight: InsightEntity) =>
    (!status || insight.spec.status === status) &&
    (selectedTypes.length === 0 ||
      selectedTypes.includes(insight.spec.resourceType)) &&
    (selectedRegions.length === 0 ||
      selectedRegions.includes(insight.spec.region)) &&
    (!query ||
      insight.spec.resource.toLowerCase().includes(query.toLowerCase()));

  const globalRows = useMemo(
    () => globalInsights.filter(matches),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [globalInsights, status, selectedTypes, selectedRegions, query],
  );
  const regionalRows = useMemo(
    () => regionalInsights.filter(matches),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [regionalInsights, status, selectedTypes, selectedRegions, query],
  );

  useEffect(() => {
    setVisibleGlobal(TABLE_STEP);
  }, [status, selectedTypes, selectedRegions, query]);
  useEffect(() => {
    setVisibleRegional(TABLE_STEP);
  }, [status, selectedTypes, selectedRegions, query]);

  const stats = useMemo(
    () => ({
      active: allInsights.filter(i => i.spec.status === 'Active').length,
      resolved: allInsights.filter(i => i.spec.status === 'Resolved').length,
      archived: allInsights.filter(i => i.spec.status === 'Archived').length,
      types: new Set(allInsights.map(i => i.spec.resourceType)).size,
    }),
    [allInsights],
  );

  const typeOptions = useMemo(
    () =>
      [...new Set(allInsights.map(i => i.spec.resourceType))].sort((a, b) =>
        a.localeCompare(b),
      ),
    [allInsights],
  );

  const regionCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    allRegions.forEach(region => {
      counts[region] = facet('region', region);
    });
    return counts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allRegions, allInsights, status, selectedTypes, selectedRegions, query]);

  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    typeOptions.forEach(type => {
      counts[type] = facet('type', type);
    });
    return counts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeOptions, allInsights, status, selectedTypes, selectedRegions, query]);

  const toggleRegion = (region: string) => {
    setSelectedRegions(prev =>
      prev.includes(region)
        ? prev.filter(item => item !== region)
        : [...prev, region],
    );
  };

  const toggleType = (type: string) => {
    setSelectedTypes(prev =>
      prev.includes(type) ? prev.filter(item => item !== type) : [...prev, type],
    );
  };

  const typeBarMax = Math.max(1, ...typeOptions.map(type => typeCounts[type] ?? 0));

  // Active findings per region drive the "by region" chart.
  const activeByRegion = useMemo(() => {
    const counts: Record<string, number> = {};
    allRegions.forEach(region => {
      counts[region] = allInsights.filter(
        insight =>
          insight.spec.region === region && insight.spec.status === 'Active',
      ).length;
    });
    return counts;
  }, [allRegions, allInsights]);
  const activeByRegionMax = Math.max(1, ...Object.values(activeByRegion));

  const openFinding = (insight: InsightEntity) => {
    setSelectedFinding(insight);
  };

  const closeFinding = () => {
    setSelectedFinding(undefined);
    // Keep the URL clean after a drawer opened from a deep link.
    if (location.search) {
      navigate(location.pathname, { replace: true });
    }
  };

  const findingRows = (insights: InsightEntity[], withRegion: boolean) =>
    insights
      .slice(0, withRegion ? visibleRegional : visibleGlobal)
      .map(finding => (
        <tr
          key={`${finding.spec.region}-${finding.metadata.name}`}
          className={styles.row}
          onClick={() => openFinding(finding)}
        >
          <td>
            <span className={styles.resName}>{finding.spec.resource}</span>
          </td>
          <td className={styles.muted}>{finding.spec.resourceType}</td>
          {withRegion && (
            <td>
              <RegionChip region={finding.spec.region} />
            </td>
          )}
          <td>{statusPill(finding.spec.status)}</td>
          <td className={styles.muted} style={{ whiteSpace: 'nowrap' }}>
            {scanLabel(finding.spec.updatedAt)}
          </td>
        </tr>
      ));

  const findingsHead = (withRegion: boolean) => (
    <thead>
      <tr>
        <th>Resource</th>
        <th>Resource type</th>
        {withRegion && <th>Region</th>}
        <th>Status</th>
        <th>Updated</th>
      </tr>
    </thead>
  );

  const allClean =
    globalRows.length === 0 &&
    regionalRows.length === 0 &&
    allInsights.filter(insight => insight.spec.status === 'Active').length === 0;

  if (isLoading) {
    return <PageSkeleton rows={8} />;
  }

  return (
    <>
      <div className={styles.statStrip}>
        <div className={`${styles.card} ${styles.stat}`}>
          <span className={styles.k}>Active findings</span>
          <span className={styles.v}>{stats.active}</span>
        </div>
        <div className={`${styles.card} ${styles.stat}`}>
          <span className={styles.k}>Resolved (90d)</span>
          <span className={styles.v}>{stats.resolved}</span>
        </div>
        <div className={`${styles.card} ${styles.stat}`}>
          <span className={styles.k}>Archived</span>
          <span className={styles.v}>{stats.archived}</span>
        </div>
        <div className={`${styles.card} ${styles.stat}`}>
          <span className={styles.k}>Resource types affected</span>
          <span className={styles.v}>{stats.types}</span>
        </div>
      </div>

      <div
        className={styles.toolbar}
        role="toolbar"
        aria-label="Insights filters"
      >
        <ToggleButtonGroup
          aria-label="Finding status"
          selectionMode="single"
          disallowEmptySelection
          selectedKeys={new Set([status === '' ? 'all' : status])}
          onSelectionChange={keys => {
            const next = ([...keys][0] as string | undefined) ?? 'all';
            setStatus(next === 'all' ? '' : (next as StatusFilter));
          }}
        >
          <ToggleButton key="all" id="all" size="small">
            All
          </ToggleButton>
          {STATUS_OPTIONS.map(option => (
            <ToggleButton key={option} id={option} size="small">
              {option}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <label className={styles.search}>
          <span aria-hidden="true">⌕</span>
          <input
            type="search"
            className={styles.searchInput}
            placeholder="Search resources…"
            aria-label="Search findings"
            value={query}
            onChange={event => setQuery(event.target.value)}
          />
        </label>

        <button
          type="button"
          ref={typeSelect.triggerRef}
          className={styles.selectBtn}
          onClick={typeSelect.toggle}
          aria-expanded={typeSelect.isOpen}
          aria-haspopup="true"
        >
          Resource type
          {selectedTypes.length > 0 && (
            <span className={styles.selectBtnCount}>
              {selectedTypes.length}
            </span>
          )}{' '}
          <span className={styles.selectBtnArrow}>▼</span>
        </button>
        <FindingPickerPopover
          options={typeOptions}
          selected={selectedTypes}
          counts={typeCounts}
          onToggle={toggleType}
          popoverProps={typeSelect.popoverProps}
        />

        <button
          type="button"
          ref={regionSelect.triggerRef}
          className={styles.selectBtn}
          onClick={regionSelect.toggle}
          aria-expanded={regionSelect.isOpen}
          aria-haspopup="true"
        >
          Region
          {selectedRegions.length > 0 && (
            <span className={styles.selectBtnCount}>
              {selectedRegions.length}
            </span>
          )}{' '}
          <span className={styles.selectBtnArrow}>▼</span>
        </button>
        <FindingPickerPopover
          options={allRegions}
          selected={selectedRegions}
          counts={regionCounts}
          onToggle={toggleRegion}
          popoverProps={regionSelect.popoverProps}
        />
      </div>

      {/* global */}
      <div className={styles.sectionHead}>
        <h2>External Access Analyzers (Global)</h2>
        <span className={styles.sectionCount}>{globalRows.length}</span>
        <span className={styles.sectionHint}>
          account-wide findings, region = global
        </span>
      </div>
      <div className={`${styles.card} ${styles.tableCard}`}>
        <table className={styles.table} aria-label="Global findings">
          {findingsHead(false)}
          <tbody>{findingRows(globalRows, false)}</tbody>
        </table>
        {globalRows.length === 0 && (
          <div className={styles.empty}>
            <b>No global findings match the current filters</b>
            Try widening the filters.
          </div>
        )}
        {globalRows.length > visibleGlobal && (
          <div className={styles.tableFooter}>
            <button
              type="button"
              className={styles.showMore}
              onClick={() => setVisibleGlobal(count => count + TABLE_STEP)}
            >
              Show more
            </button>
          </div>
        )}
      </div>

      {/* regional */}
      <div className={styles.sectionHead}>
        <h2>External Access Analyzers (Regions)</h2>
        <span className={styles.sectionCount}>{regionalRows.length}</span>
      </div>
      <div className={styles.charts}>
        <div className={`${styles.card} ${styles.chartCard}`}>
          <h3>Active findings by region</h3>
          {allRegions.map(region => (
            <FacetBar
              key={region}
              label={region}
              count={activeByRegion[region] ?? 0}
              max={activeByRegionMax}
              selected={selectedRegions.includes(region)}
              onToggle={() => toggleRegion(region)}
            />
          ))}
          {allRegions.length === 0 && (
            <div className={styles.muted}>No regional findings</div>
          )}
        </div>
        <div className={`${styles.card} ${styles.chartCard}`}>
          <h3>Findings by resource type</h3>
          {typeOptions.map(type => (
            <FacetBar
              key={type}
              label={type}
              count={typeCounts[type] ?? 0}
              max={typeBarMax}
              selected={selectedTypes.includes(type)}
              onToggle={() => toggleType(type)}
              color={TYPE_COLORS[type]}
            />
          ))}
          {typeOptions.length === 0 && (
            <div className={styles.muted}>No findings</div>
          )}
        </div>
      </div>
      <div className={`${styles.card} ${styles.tableCard}`}>
        <table className={styles.table} aria-label="Regional findings">
          {findingsHead(selectedRegions.length !== 1)}
          <tbody>
            {findingRows(regionalRows, selectedRegions.length !== 1)}
          </tbody>
        </table>
        {regionalRows.length === 0 && (
          <div className={styles.empty}>
            <b>No regional findings match the current filters</b>
            Try widening the filters.
          </div>
        )}
        {regionalRows.length > visibleRegional && (
          <div className={styles.tableFooter}>
            <button
              type="button"
              className={styles.showMore}
              onClick={() => setVisibleRegional(count => count + TABLE_STEP)}
            >
              Show more
            </button>
          </div>
        )}
      </div>

      {allClean && (
        <div className={styles.clean}>
          <span>✓</span>
          <span>
            No open external-access findings — the analyzers are clean for this
            account.
          </span>
        </div>
      )}

      {/* finding drawer (read-only) */}
      <ModernRightDrawer
        title="External access finding"
        isOpen={selectedFinding !== undefined}
        onClose={closeFinding}
        width={520}
      >
        {selectedFinding && (
          <Fragment>
            <ModernCardField
              label="Resource"
              value={selectedFinding.spec.resource}
              colSpan={{ xs: '12' }}
            />
            <ModernCardField
              label="Resource type"
              value={selectedFinding.spec.resourceType}
              colSpan={{ xs: '12', sm: '6', lg: '4' }}
            />
            <ModernCardField
              label="Region"
              value={selectedFinding.spec.region}
              colSpan={{ xs: '12', sm: '6', lg: '4' }}
            />
            <ModernCardField
              label="Status"
              value={selectedFinding.spec.status}
              colSpan={{ xs: '12', sm: '6', lg: '4' }}
            />
            <ModernCardField
              label="Updated"
              value={scanLabel(selectedFinding.spec.updatedAt)}
              colSpan={{ xs: '12', sm: '6', lg: '4' }}
            />
            <ModernCardField
              label="Analyzer run"
              value={selectedFinding.spec.reportCreationTime || '—'}
              colSpan={{ xs: '12', sm: '6', lg: '4' }}
            />
            <div>
              <div
                className={styles.sectionHint}
                style={{ fontWeight: 'bold' }}
              >
                Proposed actions (backend work)
              </div>
              <p className={styles.sectionHint}>
                The catalog mirrors analyzer findings read-only.
                Archive/Resolve would call the AWS Access Analyzer API through
                cloud-tools and re-sync — out of scope for the first iteration,
                the buttons model the target state.
              </p>
              <div className={styles.drawerButtons}>
                <button
                  type="button"
                  className={styles.disabledBtn}
                  disabled
                  title="Requires the ArchiveFinding action in cloud-tools"
                >
                  Archive finding
                </button>
                <button
                  type="button"
                  className={styles.disabledBtn}
                  disabled
                  title="Requires the resolve flow in cloud-tools"
                >
                  Mark resolved
                </button>
              </div>
            </div>
            <Flex direction="row" gap="2" align="center">
              <Link
                to={`/catalog/default/insight/${selectedFinding.metadata.name}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Text style={{ fontSize: 'var(--bui-font-size-2)', fontWeight: 'bold' }}>
                  Open full insight page ↗
                </Text>
              </Link>
              <Link
                to={`/catalog/default/cloudresource/${selectedFinding.spec.resource}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open resource page ↗
              </Link>
            </Flex>
          </Fragment>
        )}
      </ModernRightDrawer>
    </>
  );
}
